import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";

/**
 * chmeetings-style form modal: bold title + close (X) on a white header,
 * a light-grey scrollable body holding white "card" FormSections, and a
 * white footer pinned bottom-right with Cancel/Save.
 *
 * Wraps its children in a nested theme that flips TextField/Select/FormControl
 * to the underline ("standard") look with always-visible bold labels — so
 * existing field JSX inside a modal doesn't need per-field edits, only the
 * outer Dialog/DialogTitle/DialogContent/DialogActions wrapper is replaced
 * with <FormDialog>.
 */
function FormDialog({
  open,
  onClose,
  title,
  children,
  onSave,
  saveLabel,
  cancelLabel,
  saveDisabled,
  loading,
  maxWidth,
  hideFooter,
  extraActions,
}) {
  const outerTheme = useTheme();
  const ink = outerTheme.palette.ink || {};
  const formTheme = createTheme(outerTheme, {
    components: {
      MuiTextField: { defaultProps: { variant: "standard" } },
      MuiFormControl: { defaultProps: { variant: "standard" } },
      MuiSelect: { defaultProps: { variant: "standard" } },
      MuiInputLabel: {
        defaultProps: { shrink: true },
        styleOverrides: {
          root: {
            position: "relative",
            transform: "none",
            fontSize: "0.95rem",
            fontWeight: 700,
            color: ink[800] || outerTheme.palette.text.primary,
            marginBottom: 6,
            "&.Mui-focused": { color: ink[800] || outerTheme.palette.text.primary },
          },
          asterisk: { color: outerTheme.palette.error.main },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            "&::before": { borderBottomColor: ink[300] || outerTheme.palette.divider },
            "&:hover:not(.Mui-disabled, .Mui-error)::before": {
              borderBottomColor: ink[400] || outerTheme.palette.text.secondary,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { marginLeft: 0 } },
      },
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{ sx: { borderRadius: 0, overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.25,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          bgcolor: (theme) => theme.palette.ink?.[100] || theme.palette.background.default,
          p: { xs: 2, sm: 3 },
          overflowY: "auto",
          maxHeight: "70vh",
        }}
      >
        <ThemeProvider theme={formTheme}>{children}</ThemeProvider>
      </Box>

      {!hideFooter && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {extraActions}
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            disabled={loading}
            sx={{ borderRadius: 999, px: 3 }}
          >
            {cancelLabel}
          </Button>
          {onSave && (
            <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              disabled={saveDisabled || loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ borderRadius: 999, px: 3 }}
            >
              {loading ? "Saving…" : saveLabel}
            </Button>
          )}
        </Box>
      )}
    </Dialog>
  );
}

FormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node,
  onSave: PropTypes.func,
  saveLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  saveDisabled: PropTypes.bool,
  loading: PropTypes.bool,
  maxWidth: PropTypes.string,
  hideFooter: PropTypes.bool,
  extraActions: PropTypes.node,
};

FormDialog.defaultProps = {
  title: "",
  children: null,
  onSave: null,
  saveLabel: "Save",
  cancelLabel: "Cancel",
  saveDisabled: false,
  loading: false,
  maxWidth: "sm",
  hideFooter: false,
  extraActions: null,
};

export default FormDialog;
